import moment from "moment"

// « Livrer / Restituer » de la fiche dépannage (Jordan 30.10, lot 7) —
// logique pure, testable : gardiennage jusqu'à une date, frais de
// restitution hors heures (configuration), application de la restitution
// au formulaire. La modale (DepLivrerRestituerModalV2) et useDepFiche
// (bouton « Calculer ») s'en servent.

// Un séjour au parc = POI de type Parc (0) réellement sélectionné (id
// posé — un POI « jamais saisi » revient en type 0 avec id null). Le
// « Parc police » (type 7) n'est PAS un parc SVP (GE-13/GE-14).
export const POI_TYPE_PARC = 0
export const POI_TYPE_ATELIER = 8
export const estPoiParc = (po) =>
	!!po && po.id !== null && po.id !== undefined && po.type === POI_TYPE_PARC

const m = (ts) => (ts ? moment(ts, "DD-MM-YYYY HH:mm") : null)

// Règles GE-14 (décisions Jordan 25.07.2026) — calcul DÉRIVÉ de la
// chronologie du formulaire : entrée = ARRIVÉE au parc (t3, pas l'heure
// d'intervention) ; sortie = départ (t2) de la première livraison vers un
// non-parc, ou restitution, sinon `sortie` (date choisie dans la modale,
// défaut maintenant) ; parc → parc = le séjour continue ; facturation dès la
// 13ᵉ heure puis jours arrondis au SUPÉRIEUR (12 h → 0 j · 30 h → 2 j).
// `sortie` (moment) remplace une restitution déjà saisie.
// → périodes au parc [{ debut, fin, parc (libellé), heures, jours }] (Jordan
// 30.10 : détail affiché dans la modale « Livrer / Restituer »).
export const periodesGardiennage = (dep, sortie = null) => {
	let entree = null
	let parc = ""
	if (estPoiParc(dep.destination_finale)) {
		entree =
			m(dep.timestamp_3) ||
			m(dep.timestamp_2) ||
			(dep.date && dep.heure
				? moment(dep.date + " " + dep.heure, "DD-MM-YYYY HH:mm")
				: null)
		parc = dep.destination_finale.label || ""
	}
	const periodes = []
	const fermer = (fin) => {
		if (fin.isAfter(entree)) periodes.push({ debut: entree, fin, parc })
		entree = null
	}
	;(dep.livraisons || []).forEach((livraison) => {
		const depart =
			m(livraison.timestamp_2) ||
			m(livraison.timestamp_0) ||
			(livraison.date && livraison.heure
				? moment(
						livraison.date + " " + livraison.heure,
						"DD-MM-YYYY HH:mm",
					)
				: null)
		const arrivee = m(livraison.timestamp_3) || depart
		const versParc = estPoiParc(livraison.destination_adresse)
		if (entree !== null) {
			// Parc → parc (manque de place) : toujours au parc, le séjour
			// continue ; revue des lots 7-8 (🟡6) : le détail change de parc à
			// l'arrivée (heures continues, total inchangé).
			if (versParc) {
				if (arrivee !== null && arrivee.isAfter(entree)) {
					periodes.push({ debut: entree, fin: arrivee, parc })
					entree = arrivee
				}
				parc = livraison.destination_adresse.label || ""
				return
			}
			if (depart === null) return
			fermer(depart)
		} else if (versParc && arrivee !== null) {
			entree = arrivee
			parc = livraison.destination_adresse.label || ""
		}
	})
	// Restitution client = sortie du parc (date choisie, sinon saisie).
	const resti =
		sortie ||
		(dep.restiDate
			? moment(
					dep.restiDate + " " + (dep.restiHeure || "00:00"),
					"DD-MM-YYYY HH:mm",
				)
			: null)
	if (entree !== null && resti !== null) fermer(resti)
	// Toujours au parc : la période court jusqu'à maintenant.
	if (entree !== null) fermer(moment())
	return periodes.map((p) => {
		const heures = p.fin.diff(p.debut, "minutes") / 60
		return { ...p, heures, jours: joursPourHeures(heures) }
	})
}

// Facturation dès la 13ᵉ heure, jours arrondis au supérieur.
export const joursPourHeures = (heures) =>
	heures <= 12 ? 0 : Math.ceil(heures / 24)

// → jours facturés : sur le CUMUL des heures au parc (pas la somme des jours
// de chaque période).
export const joursGardiennage = (dep, sortie = null) =>
	joursPourHeures(
		periodesGardiennage(dep, sortie).reduce((s, p) => s + p.heures, 0),
	)

// Frais de restitution hors heures : configuration de l'entreprise
// (master.extra_data.tarification.depannage, /configuration › Identité).
// Montant 0 = désactivé (défaut, Jordan 30.10).
export const HORS_HEURES_DEFAUT = {
	montant: 0,
	debut: "07:00",
	fin: "18:30",
	weekend: true,
}

export const configHorsHeures = (masterEntite) => {
	const t =
		(masterEntite &&
			masterEntite.extra_data &&
			masterEntite.extra_data.tarification &&
			masterEntite.extra_data.tarification.depannage) ||
		{}
	const montant = parseFloat(t.restitutionHorsHeures)
	return {
		montant: Number.isFinite(montant) && montant > 0 ? montant : 0,
		debut: t.restitutionDebut || HORS_HEURES_DEFAUT.debut,
		fin: t.restitutionFin || HORS_HEURES_DEFAUT.fin,
		weekend:
			t.restitutionWeekend === undefined
				? HORS_HEURES_DEFAUT.weekend
				: !!t.restitutionWeekend,
	}
}

// → montant des frais pour une restitution à `quand` (moment), ou 0. Hors
// heures = avant `debut` ou après `fin` (bornes incluses dans la plage), ou
// le week-end si coché.
export const fraisHorsHeures = (config, quand) => {
	if (!config || !(config.montant > 0) || !quand) return 0
	const hhmm = quand.format("HH:mm")
	const weekend = config.weekend && quand.isoWeekday() >= 6
	return weekend || hhmm < config.debut || hhmm > config.fin
		? config.montant
		: 0
}

const round2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100

// Payeurs de la ligne « Restitution » (TransactionV2.payeParSelect) ; en
// dépannage, « Assurance » se dit « Assistance » (GE-60).
export const PAYEUR_CLIENT = 1
export const PAYEUR_ASSISTANCE = 0
export const PAYEURS_RESTITUTION = [
	[PAYEUR_CLIENT, "Client"],
	[PAYEUR_ASSISTANCE, "Assistance"],
]

// Applique la restitution au formulaire → nouvelle fiche (rien n'est sauvé).
// `choix` : { reprisPar, date "YYYY-MM-DD", heure "HH:mm", gardQ, gardT,
// frais, encaissement "paye" | "rien" | "autrement", moyenPaie, caissier,
// payeur (1 Client, défaut | 0 Assistance — comme le bloc Transactions ; la
// Perte relève de « Réglé autrement »), montant }. Une ligne « Restitution »
// (sans date, quittance après le save, « Repris par » dans ses détails) n'est
// créée que pour « Payé maintenant » et un montant > 0.
export const appliquerRestitution = (dep, choix) => {
	const quand = moment(choix.date + " " + choix.heure, "YYYY-MM-DD HH:mm")
	const next = { ...dep }
	next.restiDate = quand.format("DD-MM-YYYY")
	next.restiHeure = quand.format("HH:mm")
	next.restiReprisPar = choix.reprisPar || ""
	next.restiTot = round2(choix.frais)
	next.gardQ = parseInt(choix.gardQ) || 0
	next.gardT = round2(choix.gardT)
	next.gardTot = round2(next.gardQ * next.gardT)
	let transaction = null
	if (choix.encaissement === "paye" && round2(choix.montant) > 0) {
		transaction = {
			id: Math.random(),
			date: "",
			payeParSelect:
				choix.payeur === PAYEUR_ASSISTANCE
					? PAYEUR_ASSISTANCE
					: PAYEUR_CLIENT,
			quittance: null,
			facture: null,
			moyenPaie: choix.moyenPaie,
			caissier: choix.caissier,
			total: round2(choix.montant),
			details: choix.reprisPar || "",
			raison: "Restitution",
			depannage: next.id,
			dep_mission: null,
		}
		next.transactionsv2 = [...(dep.transactionsv2 || []), transaction]
	}
	return { dep: next, transaction }
}
